/**
 * Variable Provider
 *
 * Unified system for building template variables from multiple data sources.
 * Supports standard variables, client-configured variables, and computed formulas.
 *
 * =============================================================================
 * DATA SOURCES (in priority order, later sources override earlier)
 * =============================================================================
 * 1. Default/legacy data (basis, interest, etc.)
 * 2. Calculator result (breakdown per-class, fundMetrics, etc.)
 * 3. Client config (clientData.templateVariables)
 * 4. Class config (clientData.equity[key].templateVariables)
 * 5. Aggregates (calculated totals from cap table)
 * 6. Calculator customVariables (for extension)
 *
 * =============================================================================
 * VARIABLE CONFIG FORMAT
 * =============================================================================
 * {
 *   "variableName": {
 *     "source": "path.to.value" | "aggregate.field",
 *     "type": "currency" | "number" | "percentage" | "raw",
 *     "formula": "var1 / var2 * 100"  // optional computed value
 *   }
 * }
 */

import { TemplateUtils } from '../helpers/TemplateUtils.js';

const VariableProvider = {
	// ========================================
	// STANDARD VARIABLE TYPES
	// ========================================
	// Base types that are always available. Client config can add more.

	standardTypes: {
		// Share counts
		owned: 'number',
		approved: 'number',
		vested: 'number',
		unvested: 'number',
		orig: 'number',
		tranches: 'number',

		// Currency values
		committed: 'currency',
		basis: 'currency',
		value: 'currency',
		interest: 'currency',
		uncalled: 'currency',
		vestedValue: 'currency',
		unvestedValue: 'currency',
		sharePrice: 'currency',
		strikePrice: 'currency',

		// Income allocations
		taxBasis: 'currency',
		incomeAllocations: 'currency',
		contributions: 'currency',
		distributions: 'currency',
		marketValue: 'currency',
		currentValue: 'currency',
		unrealizedGain: 'currency',

		// Percentage values
		vestedPercent: 'percentage',
		ownershipPercent: 'percentage',

		// Multipliers
		tvpi: 'multiplier',

		// Text/labels
		name: 'raw',
		sharesType: 'raw',
		youownLabel: 'raw',
		costbasisLabel: 'raw',
		uncalledLabel: 'raw',
		tradeAction: 'raw',
		expDate: 'raw',
		stockStatus: 'raw',
		unitholderID: 'raw',
		userid: 'raw',

		// Booleans
		hasVesting: 'raw',
		isDebt: 'raw',
		hasCalculator: 'raw',

		// Aggregates
		totalClassShares: 'number',
		totalClassValue: 'currency',
		shareholderCount: 'number',

		// Numeric (eligibility, etc.)
		eligible: 'number',
	},

	// ========================================
	// SOURCE PATH RESOLUTION
	// ========================================

	/**
	 * Safely resolve a dot-notation path from an object
	 * e.g., "userData.clients.foo.status" -> userData.clients.foo.status value
	 *
	 * @param {Object} obj - Source object
	 * @param {string} path - Dot-notation path
	 * @param {Object} context - Context with special keys like {client}
	 * @returns {*} - Resolved value or undefined
	 */
	resolvePath: function (obj, path, context = {}) {
		if (!obj || !path || typeof path !== 'string') return undefined;

		// Replace {client} placeholder with actual client name
		if (context.client) {
			path = path.replace(/\{client\}/g, context.client);
		}

		const parts = path.split('.');
		let current = obj;

		for (const part of parts) {
			if (current === undefined || current === null) return undefined;
			if (!Object.prototype.hasOwnProperty.call(current, part)) return undefined;
			current = current[part];
		}

		return current;
	},

	/**
	 * Evaluate a simple formula with variable substitution
	 * Supports: +, -, *, /
	 * Example: "(owned / totalClassShares) * 100"
	 *
	 * @param {string} formula - Formula string
	 * @param {Object} vars - Current variable map
	 * @returns {number|null} - Computed value or null on error
	 */
	evaluateFormula: function (formula, vars) {
		if (!formula || typeof formula !== 'string') return null;

		try {
			// Replace variable names with their numeric values
			// Match word characters that aren't operators
			let expression = formula;

			// Extract all variable references (alphanumeric + underscore)
			const varNames = formula.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [];

			for (const varName of varNames) {
				const value = TemplateUtils.safeGetVar(vars, varName);
				if (value === undefined || value === null) return null;

				const numValue = Number(value);
				if (isNaN(numValue)) return null;

				// Replace the variable with its numeric value
				// Use word boundary to avoid partial replacements
				const regex = new RegExp('\\b' + varName + '\\b', 'g');
				expression = expression.replace(regex, numValue.toString());
			}

			// Validate: only allow numbers, operators, parentheses, spaces, dots
			if (!/^[\d\s+\-*/().]+$/.test(expression)) {
				console.warn('[VariableProvider] Invalid formula expression:', formula);
				return null;
			}

			// Evaluate the expression safely
			// Using Function constructor is safer than eval when input is validated
			const result = Function('"use strict"; return (' + expression + ')')();

			if (typeof result !== 'number' || isNaN(result) || !isFinite(result)) {
				return null;
			}

			return result;
		} catch (e) {
			console.warn('[VariableProvider] Error evaluating formula:', formula, e);
			return null;
		}
	},

	// ========================================
	// VARIABLE BUILDING (scaffolding for future HeadlineFormatter integration)
	// ========================================
	// NOTE: buildClassVariables and buildPortfolioVariables are currently unused.
	// EquityCardFormatter.buildVariableMap handles variable building directly.
	// These methods are retained for potential HeadlineFormatter integration.

	/**
	 * Build variables for equity card templates
	 *
	 * @param {Object} context - All data sources
	 * @param {Object} context.userShares - User's share data for this class
	 * @param {Object} context.equityConfig - Client's equity config for this class
	 * @param {Object} context.calculatorData - Per-class calculator breakdown
	 * @param {Object} context.aggregates - Cap table aggregates for this class
	 * @param {Object} context.userData - Full user data object
	 * @param {Object} context.clientData - Full client data object
	 * @param {string} context.classKey - Current share class key
	 * @param {Object} context.legacy - Legacy values (basis, value, interest)
	 * @returns {Object} - { vars: Object, types: Object }
	 */
	buildClassVariables: function (context) {
		const {
			userShares = {},
			equityConfig = {},
			calculatorData = {},
			aggregates = {},
			userData = {},
			clientData = {},
			classKey = '',
			legacy = {},
		} = context;

		const vars = {};
		const types = { ...this.standardTypes };

		// 1. Legacy/default values
		if (legacy.basis !== undefined) vars.basis = Number(legacy.basis) || 0;
		if (legacy.value !== undefined) vars.value = Number(legacy.value) || 0;
		if (legacy.interest !== undefined) vars.interest = Number(legacy.interest) || 0;

		// 2. User share data
		vars.owned = Number(userShares.owned || 0);
		vars.approved = Number(userShares.approved || 0);
		vars.committed = Number(userShares.committed || 0);
		vars.orig = Number(userShares.orig || 0);
		vars.tranches = Number(userShares.tranches || 0);

		// 3. Equity config labels
		vars.name = equityConfig.name || classKey;
		vars.sharesType = equityConfig.sharesType || 'shares';
		vars.youownLabel = equityConfig.youownLabel || 'You Own';
		vars.costbasisLabel = equityConfig.costbasisLabel || 'Cost Basis';
		vars.uncalledLabel = equityConfig.uncalledLabel || 'Uncalled';

		// 4. Stock status from userData (Fishbeck pattern)
		const client = clientData.code || clientData.name || '';
		if (userData.clients && userData.clients[client]) {
			vars.stockStatus = userData.clients[client].status || '';

			// Calculate eligible based on stock status
			const statusStr = String(vars.stockStatus || '');
			const statusNum = parseFloat(statusStr);
			if (!isNaN(statusNum) && statusNum > 0) {
				vars.eligible = statusNum;
			} else {
				vars.eligible = vars.owned;
			}
		}

		// 5. User ID
		vars.userid = userData.userid || '';
		vars.unitholderID = userData.userid || '';

		// 6. Calculator data (per-class breakdown)
		if (calculatorData) {
			if (calculatorData.vested !== undefined) vars.vested = Number(calculatorData.vested);
			if (calculatorData.vestedValue !== undefined) vars.vestedValue = Number(calculatorData.vestedValue);
			if (calculatorData.unvestedValue !== undefined) vars.unvestedValue = Number(calculatorData.unvestedValue);
			if (calculatorData.sharePrice !== undefined) vars.sharePrice = Number(calculatorData.sharePrice);
			if (calculatorData.strikePrice !== undefined) vars.strikePrice = Number(calculatorData.strikePrice);

			// Income allocations variables
			if (calculatorData.taxBasis !== undefined) vars.taxBasis = Number(calculatorData.taxBasis);
			if (calculatorData.incomeAllocations !== undefined) vars.incomeAllocations = Number(calculatorData.incomeAllocations);
			if (calculatorData.marketValue !== undefined) vars.marketValue = Number(calculatorData.marketValue);
			if (calculatorData.currentValue !== undefined) vars.currentValue = Number(calculatorData.currentValue);
			if (calculatorData.unrealizedGain !== undefined) vars.unrealizedGain = Number(calculatorData.unrealizedGain);
			if (calculatorData.contributions !== undefined) vars.contributions = Number(calculatorData.contributions);
			if (calculatorData.distributions !== undefined) vars.distributions = Number(calculatorData.distributions);

			// TVPI for fund classes
			if (calculatorData.tvpi !== undefined) vars.tvpi = Number(calculatorData.tvpi);

			// Calculator custom variables
			if (calculatorData.customVariables) {
				Object.entries(calculatorData.customVariables).forEach(([key, value]) => {
					vars[key] = value;
					// Infer type if not already defined
					if (!types[key]) {
						types[key] = typeof value === 'number' ? 'number' : 'raw';
					}
				});
			}
		}

		// Calculate unvested if we have vested
		if (vars.vested !== undefined) {
			vars.unvested = Math.max(0, vars.owned - vars.vested);
		}

		// Calculate vested percent
		if (vars.vested !== undefined && vars.owned > 0) {
			vars.vestedPercent = (vars.vested / vars.owned) * 100;
		}

		// Boolean flags
		vars.hasVesting = vars.vested !== undefined && vars.vested !== vars.owned;
		vars.hasCalculator = !!calculatorData && Object.keys(calculatorData).length > 0;
		vars.isDebt = equityConfig.debt === true;

		// 7. Aggregate data (cap table totals)
		if (aggregates) {
			if (aggregates.totalOwned !== undefined) {
				vars.totalClassShares = Number(aggregates.totalOwned);
			}
			if (aggregates.totalValue !== undefined) {
				vars.totalClassValue = Number(aggregates.totalValue);
			}
			if (aggregates.shareholderCount !== undefined) {
				vars.shareholderCount = Number(aggregates.shareholderCount);
			}
		}

		// 8. Client-configured template variables (clientData.templateVariables)
		if (clientData.templateVariables) {
			this._applyConfiguredVariables(vars, types, clientData.templateVariables, {
				userData,
				clientData,
				aggregates,
				client,
			});
		}

		// 9. Class-configured template variables (equityConfig.templateVariables)
		if (equityConfig.templateVariables) {
			this._applyConfiguredVariables(vars, types, equityConfig.templateVariables, {
				userData,
				clientData,
				aggregates,
				client,
				vars, // Pass current vars for formula evaluation
			});
		}

		// 10. Compute ownershipPercent if we have the data
		if (vars.owned !== undefined && vars.totalClassShares && vars.totalClassShares > 0) {
			vars.ownershipPercent = (vars.owned / vars.totalClassShares) * 100;
		}

		return { vars, types };
	},

	/**
	 * Apply configured variables from templateVariables config
	 *
	 * @param {Object} vars - Variable map to populate
	 * @param {Object} types - Type map to populate
	 * @param {Object} config - templateVariables config object
	 * @param {Object} sources - Data sources for path resolution
	 */
	_applyConfiguredVariables: function (vars, types, config, sources) {
		if (!config || typeof config !== 'object') return;

		const { userData, clientData, aggregates, client, vars: currentVars } = sources;

		Object.entries(config).forEach(([varName, varConfig]) => {
			// Validate variable name
			if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(varName)) {
				console.warn('[VariableProvider] Invalid variable name in config:', varName);
				return;
			}

			if (typeof varConfig !== 'object' || varConfig === null) return;

			const { source, type, formula } = varConfig;

			// Register type
			if (type) {
				types[varName] = type;
			}

			// Compute from formula
			if (formula) {
				// Use current vars merged with any new vars we've set
				const mergedVars = { ...vars, ...(currentVars || {}) };
				const computed = this.evaluateFormula(formula, mergedVars);
				if (computed !== null) {
					vars[varName] = computed;
				}
				return;
			}

			// Resolve from source path
			if (source) {
				let value;

				// Handle special source prefixes
				if (source.startsWith('userData.')) {
					value = this.resolvePath(userData, source.replace('userData.', ''), { client });
				} else if (source.startsWith('clientData.')) {
					value = this.resolvePath(clientData, source.replace('clientData.', ''), { client });
				} else if (source.startsWith('aggregate.') || source.startsWith('aggregates.')) {
					const aggPath = source.replace(/^aggregates?\./, '');
					value = this.resolvePath(aggregates, aggPath, { client });
				} else {
					// Direct path (could be any source)
					value = this.resolvePath({ userData, clientData, aggregates }, source, { client });
				}

				if (value !== undefined) {
					vars[varName] = value;
				}
			}
		});
	},

	// ========================================
	// PORTFOLIO-LEVEL VARIABLES
	// ========================================

	/**
	 * Build variables for portfolio/headline templates
	 * Similar to buildClassVariables but for portfolio-wide context
	 *
	 * @param {Object} context - Data sources
	 * @param {Object} context.calculatorResult - Full calculator result
	 * @param {Object} context.userData - User data
	 * @param {Object} context.clientData - Client data
	 * @param {Object} context.legacyData - Legacy portfolio totals
	 * @returns {Object} - { vars: Object, types: Object }
	 */
	buildPortfolioVariables: function (context) {
		const { calculatorResult = {}, userData = {}, clientData = {}, legacyData = {} } = context;

		const vars = {};
		const types = { ...this.standardTypes };

		// Legacy portfolio values
		if (legacyData.totalPaidInCapital !== undefined) vars.totalPaidInCapital = Number(legacyData.totalPaidInCapital);
		if (legacyData.totalCurrentValue !== undefined) vars.totalCurrentValue = Number(legacyData.totalCurrentValue);
		if (legacyData.totalDistributions !== undefined) vars.totalDistributions = Number(legacyData.totalDistributions);
		if (legacyData.totalCash !== undefined) vars.totalCash = Number(legacyData.totalCash);

		// Calculator results
		if (calculatorResult.totalValue !== undefined) vars.totalValue = Number(calculatorResult.totalValue);
		if (calculatorResult.vestedValue !== undefined) vars.vestedValue = Number(calculatorResult.vestedValue);
		if (calculatorResult.unvestedValue !== undefined) vars.unvestedValue = Number(calculatorResult.unvestedValue);
		if (calculatorResult.totalContributions !== undefined) vars.totalContributions = Number(calculatorResult.totalContributions);
		if (calculatorResult.totalDistributions !== undefined) vars.totalDistributions = Number(calculatorResult.totalDistributions);

		// Fund metrics
		if (calculatorResult.fundMetrics) {
			const fm = calculatorResult.fundMetrics;
			if (fm.tvpi !== undefined) vars.tvpi = Number(fm.tvpi);
			if (fm.dpi !== undefined) vars.dpi = Number(fm.dpi);
			if (fm.rvpi !== undefined) vars.rvpi = Number(fm.rvpi);
			if (fm.moic !== undefined) vars.moic = Number(fm.moic);
			if (fm.pic !== undefined) vars.pic = Number(fm.pic);
		}

		// Calculator custom variables
		if (calculatorResult.customVariables) {
			Object.entries(calculatorResult.customVariables).forEach(([key, value]) => {
				vars[key] = value;
				if (!types[key]) {
					types[key] = typeof value === 'number' ? 'number' : 'raw';
				}
			});
		}

		// User/client data
		const client = clientData.code || clientData.name || '';
		vars.userid = userData.userid || '';
		vars.unitholderID = userData.userid || '';

		if (userData.clients && userData.clients[client]) {
			vars.stockStatus = userData.clients[client].status || '';
		}

		// Labels
		vars.labelPaidIn = clientData.language?.labelPaidIn || 'Paid-In Capital';
		vars.accountType = clientData.language?.accountType || '';

		// Client-configured variables
		if (clientData.templateVariables) {
			this._applyConfiguredVariables(vars, types, clientData.templateVariables, {
				userData,
				clientData,
				client,
				vars,
			});
		}

		return { vars, types };
	},

	/**
	 * Get the type for a variable, checking dynamic types first
	 *
	 * @param {string} varName - Variable name
	 * @param {Object} dynamicTypes - Additional types from config
	 * @returns {string} - Variable type
	 */
	getType: function (varName, dynamicTypes = {}) {
		return dynamicTypes[varName] || this.standardTypes[varName] || 'raw';
	},
};

export { VariableProvider };
